/**
 * Breakpoint values for responsive design
 * Based on common device widths
 *
 * Devices reference:
 * - iPhone SE: 375px
 * - iPhone 14: 390px
 * - iPhone 14 Pro Max: 430px
 * - iPad Mini: 744px (portrait), 1024px (landscape)
 * - iPad Air/Pro 11": 820px (portrait), 1180px (landscape)
 * - iPad Pro 12.9": 1024px (portrait), 1366px (landscape)
 */
export const breakpoints = {
  xs: 375, // Small phones (iPhone SE, older phones)
  sm: 640, // Large phones / Small tablets
  md: 834, // iPad Mini landscape / iPad portrait
  lg: 1200, // Desktop starts here (above iPad Air landscape 1180px)
  xl: 1400, // Large desktops
  '2xl': 1536, // Extra large desktops
} as const;

/**
 * Media query helpers for styled-components
 * Usage: ${media.mobile} { ... }
 */
export const media = {
  // Mobile first (min-width)
  xs: `@media (min-width: ${breakpoints.xs}px)`,
  sm: `@media (min-width: ${breakpoints.sm}px)`,
  md: `@media (min-width: ${breakpoints.md}px)`,
  lg: `@media (min-width: ${breakpoints.lg}px)`,
  xl: `@media (min-width: ${breakpoints.xl}px)`,
  '2xl': `@media (min-width: ${breakpoints['2xl']}px)`,

  // Max-width queries (desktop first)
  maxXs: `@media (max-width: ${breakpoints.xs - 1}px)`,
  maxSm: `@media (max-width: ${breakpoints.sm - 1}px)`,
  maxMd: `@media (max-width: ${breakpoints.md - 1}px)`,
  maxLg: `@media (max-width: ${breakpoints.lg - 1}px)`,
  maxXl: `@media (max-width: ${breakpoints.xl - 1}px)`,

  // Device-specific shortcuts
  mobile: `@media (max-width: ${breakpoints.sm - 1}px)`, // < 640px (phones)
  tablet: `@media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.lg - 1}px)`, // 640-1199px (tablets, including iPad Air landscape)
  tabletUp: `@media (min-width: ${breakpoints.sm}px)`, // >= 640px
  tabletDown: `@media (max-width: ${breakpoints.lg - 1}px)`, // < 1200px (phones + tablets + iPad Air landscape)
  desktop: `@media (min-width: ${breakpoints.lg}px)`, // >= 1200px
  largeDesktop: `@media (min-width: ${breakpoints.xl}px)`, // >= 1400px

  // Touch devices
  touch: '@media (hover: none) and (pointer: coarse)',

  // Reduced motion
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
} as const;

export type Breakpoint = keyof typeof breakpoints;
export type MediaQuery = keyof typeof media;
